import React, {
  FC,
  useCallback,
  ChangeEventHandler,
  useMemo,
  useRef,
  useState,
}                              from 'react';
import { useField }            from 'formik';
import {
  Button,
  Typography,
  IconButton,
  Link,
  Grid,
  Theme,
  ButtonProps,
}                              from '@material-ui/core';
import {
  makeStyles,
  createStyles,
}                              from '@material-ui/core/styles';
import { ClearRounded }        from '@material-ui/icons';
import { EUploadTypes }        from '@enums/common';
import { useToggle }           from '@utils/useToggle';
import { FILE_ACCEPT_BY_TYPE } from '@components/FileUpload/utils/suggestions';
import { ILogo }               from '@typings/accountManagement';
import { ICardAd }             from '@typings/nationalAdManagement';

const useStyles = makeStyles<Theme, Pick<ButtonProps, 'color'>>(({ palette }) => createStyles({
  labelText : { color: palette.grey[700] },
  button    : ({ color }) => {
    let backgroundColor: string;

    switch (color) {
      case 'default':
        backgroundColor = palette.common.white;
        break;
      case 'primary':
      case 'secondary':
        backgroundColor = palette[color].main;
        break;
      default:
        backgroundColor = color ?? 'inherit';
        break;
    }

    return {
      margin : '0 15px 0 0',
      color  : (color === 'primary' || color === 'secondary') ? palette.common.white : palette.common.black,
      backgroundColor,

      '&:hover': { backgroundColor },
    };
  },
}));

// Function that returns a promise to read the file as dataURL
const getFileData = (file: File): Promise<any> =>
  new Promise(
    (resolve): void => {
      const fileReader = new FileReader();

      fileReader.onload = (): void => resolve(fileReader.result);

      fileReader.readAsDataURL(file);
    },
  );

// Function that returns a promise to get image dimension ({ width, height })
const getImageDimension = (data: string): any =>
  new Promise(
    (resolve): void => {
      const image = new Image();

      image.onload = (): void => {
        const { width, height } = image;
        resolve({ width, height });
      };

      image.src = data;
    },
  );

const humanFileSize = (bytes: number, si = false): string => {
  const thresh   = si ? 1000 : 1024;
  const UNITS    = ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  const UNITS_SI = ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  if (Math.abs(bytes) < thresh) {
    return `${bytes} B`;
  }

  const units = si ? UNITS_SI : UNITS;

  let u = 0;
  let bytesSize = bytes;

  for (u; u < units.length - 1; u++) {
    bytesSize /= thresh;
    if (Math.abs(bytesSize) < thresh) break;
  }

  return `${bytesSize.toFixed(1)} ${units[u]}`;
};

const imageTypeRegex = new RegExp('^image/');

interface IFileInfo {
  fileSize : string;
  height   : number;
  width    : number;
}

interface IProps extends Omit<ButtonProps, 'name'> {
  name      : string;
  fileType  : EUploadTypes;
  label     : string;
  withSize? : boolean;
}

export const FileUpload: FC<IProps> = ({
  name,
  fileType,
  disabled,
  label    = 'Upload',
  size     = 'medium',
  variant  = 'contained',
  color    = 'secondary',
  withSize = false,
  children,
  ...restProps
}) => {
  const { button, labelText } = useStyles({ color });

  const [field, , helpers] = useField<File | ILogo | ICardAd | null>(name);

  const [fileInfo, setFileInfo]      = useState<IFileInfo | null>(null);
  const [isLoading, toggleIsLoading] = useToggle();

  const inputRef = useRef<HTMLInputElement>(null);

  const isUploadDisabled = useMemo<boolean>(
    () => disabled || isLoading,
    [disabled, isLoading],
  );

  const onBtnClick = useCallback(
    (): void => {
      if (!inputRef.current) {
        return;
      }

      inputRef.current.click();
    },
    [],
  );

  const parseFile = useCallback(
    async (file: File): Promise<void> => {
      const data = await getFileData(file);

      let dimension  = null;
      const fileSize = humanFileSize(file.size);

      if (data && imageTypeRegex.test(file.type)) {
        dimension = await getImageDimension(data);
      }

      setFileInfo({
        ...dimension,
        fileSize,
      });
    },
    [],
  );

  const onInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    async (event): Promise<void> => {
      toggleIsLoading();

      const [file] = event.target.files;

      if (file && withSize) {
        await parseFile(file);
      }

      helpers.setValue(file);
      toggleIsLoading();
    },
    [helpers, parseFile, toggleIsLoading, withSize],
  );

  const onResetFile = useCallback(
    (): void => {
      helpers.setValue(null);
      setFileInfo(null);
    },
    [helpers],
  );

  const fileName = useMemo<string>(
    () => {
      const { value } = field;

      if (!value) {
        return '';
      }

      if ('name' in value) {
        return value.name;
      }

      if ('fileName' in value) {
        return value.fileName;
      }

      return '';
    },
    [field],
  );

  const fileLink = useMemo<string>(
    () => {
      const { value } = field;

      if (!value) {
        return '';
      }

      if ('storageUrl' in value) {
        return value.storageUrl;
      }

      if ('imageUrl' in value) {
        return value.imageUrl;
      }

      return window.URL.createObjectURL(value);
    },
    [field],
  );

  return (
    <Grid container alignItems="center">
      <Grid item xs={4}>
        <Button
          {...restProps}
          size    = {size}
          variant = {variant}
          classes = {{ root: button }}
          onClick = {onBtnClick}
        >
          {isLoading ? 'Loading...' : (children || label)}

          <input
            ref      = {inputRef}
            type     = "file"
            accept   = {FILE_ACCEPT_BY_TYPE[fileType]}
            disabled = {isUploadDisabled}
            onChange = {onInputChange}
            hidden
          />
        </Button>
      </Grid>

      {field.value ? (
        <Grid item xs={9}>
          <Grid
            wrap       = "nowrap"
            alignItems = "center"
            container
          >
            <Link
              href  = {fileLink}
              color = "secondary"
            >
              {fileName}
            </Link>

            <IconButton
              color   = "secondary"
              onClick = {onResetFile}
            >
              <ClearRounded />
            </IconButton>
          </Grid>
        </Grid>
      ) : (
        <Grid item xs={9}>
          <Typography
            classes   = {{ root: labelText }}
            component = "span"
          >
            No file chosen
          </Typography>
        </Grid>
      )}

      {withSize && fileInfo && (
        <Grid item xs={12}>
          <Typography classes={{ root: labelText }}>
            <span>Graphic size: {fileInfo.width}px * {fileInfo.height}px</span>
            &nbsp;
            <span>({fileInfo.fileSize})</span>
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};
